/* AIMA · freiwilliger Ersatzwunsch · nur eine Bestellnotiz: 17.10.2026
   Ziel: assets/aima-replacement-preference.js */
(() => {
  const tag = 'aima-replacement-preference';
  if (customElements.get(tag)) return;
  const VERSION = '2026-10-01.v2';
  const KEYS = {
    choice: 'aima_replacement_choice', source: 'aima_replacement_source',
    version: 'aima_replacement_version', label: 'Ersatzwunsch',
    wishes: 'Ersatz-Hinweis', phone: 'Ersatz-Rückrufnummer',
    block: 'aima_replacement_note_block'
  };
  const TITLES = {
    replace: 'Ähnlichen Artikel erhalten', contact: 'Bitte vorher anrufen',
    refund: 'Geld für fehlenden Artikel zurück', wait: 'Ich kann warten'
  };
  // Nur zum Übernehmen bereits gespeicherter Warenkörbe der bisherigen Version.
  const LEGACY_LABELS = {
    replace: 'Ähnlichen Artikel erhalten – mindestens gleicher Wert, ohne Aufpreis; ohne passenden Ersatz Artikelpreis erstatten',
    contact: 'Bitte vorher anrufen – Ersatz nur nach telefonischer Zustimmung',
    refund: 'Geld für fehlenden Artikel zurück – kein Ersatz; Betrag für fehlende Artikel erstatten',
    wait: 'Ich kann warten – auf fehlende Artikel warten; bitte nicht erstatten'
  };
  const LABELS = {
    replace: 'Passenden Ersatz beilegen (mindestens gleicher Wert, ohne Aufpreis). Sonst erstatten.',
    contact: 'Vor Ersatz anrufen und Zustimmung einholen.',
    refund: 'Kein Ersatz, Betrag erstatten.',
    wait: 'Auf den Artikel warten, nicht erstatten.'
  };
  const NOTE_PREFIX = 'Bei fehlenden Artikeln: ';
  const WISHES_PREFIX = '\nWünsche zum Ersatz: ';
  const instances = new Set();
  const forms = new WeakSet();
  const continuing = new WeakSet();
  const resubmitting = new WeakSet();
  const CHECKOUT_SAVE_WAIT_MS = 1500;
  const knownBlocks = new Set();
  const text = value => String(value ?? '').replace(/\r\n/g, '\n');
  const cleanWishes = value => text(value).slice(0, 300);
  const same = (a, b) => a.choice === b.choice && a.wishes === b.wishes && a.customerNote === b.customerNote;
  let confirmed = null;
  let draft = null;
  let saving = null;
  let saveController = null;
  let saveTimer;
  let needsRetry = false;
  let phase = '';
  let revision = 0;

  const blockFor = state => !state.choice ? '' :
    NOTE_PREFIX + LABELS[state.choice] +
    (state.choice === 'replace' && state.wishes.trim() ? WISHES_PREFIX + state.wishes.trim() : '');
  const orderNoteFor = state => {
    const block = blockFor(state);
    return state.customerNote + (state.customerNote && block ? '\n\n' : '') + block;
  };
  // Leere Werte entfernen ausschließlich unsere bisherigen Zusatzattribute.
  // Zapiet-Attribute und andere Warenkorbdaten werden nicht überschrieben.
  const attributesFor = () => Object.fromEntries(Object.values(KEYS).map(key => [key, '']));

  function preferenceFromNote(value) {
    const note = text(value);
    let match = null;
    for (const [choice, label] of Object.entries(LABELS)) {
      const heading = NOTE_PREFIX + label;
      let at = note.indexOf(heading);
      while (at !== -1) {
        // Nur einen vollständigen, von uns erzeugten Schlussabsatz erkennen.
        // Andere Sätze oder Hinweise vor/nach einem ähnlichen Text bleiben frei.
        const boundary = at === 0 || note.slice(at - 2, at) === '\n\n';
        const tail = note.slice(at + heading.length);
        const hasWishes = choice === 'replace' && tail.startsWith(WISHES_PREFIX);
        const wishes = hasWishes ? tail.slice(WISHES_PREFIX.length) : '';
        if (boundary && (tail === '' || (hasWishes && wishes.length <= 300)) &&
            (!match || at < match.at)) {
          match = { at, choice, wishes, block: note.slice(at) };
        }
        at = note.indexOf(heading, at + heading.length);
      }
    }
    return match;
  }

  function withoutBlock(value, block) {
    // Entfernt nur den exakt zuvor erzeugten Absatz; andere Kundentexte bleiben erhalten.
    const note = text(value);
    if (!block) return note;
    const at = note.lastIndexOf(block);
    if (at < 0 || (at > 0 && note[at - 1] !== '\n')) return note;
    const end = at + block.length;
    if (end < note.length && note[end] !== '\n') return note;
    const start = at >= 2 && note.slice(at - 2, at) === '\n\n' ? at - 2 : at;
    return note.slice(0, start) + note.slice(end);
  }

  function customerNoteFrom(note, attributes = {}) {
    let result = text(note);
    const serverBlock = text(attributes[KEYS.block]);
    if (serverBlock) knownBlocks.add(serverBlock);
    const saved = preferenceFromNote(result);
    if (saved) knownBlocks.add(saved.block);
    for (const block of Array.from(knownBlocks).sort((a, b) => b.length - a.length)) {
      result = withoutBlock(result, block);
    }
    return result;
  }

  function stateFrom(note, attributes = {}) {
    const saved = preferenceFromNote(note);
    let choice = attributes[KEYS.version] === VERSION && attributes[KEYS.source] === 'customer' &&
      Object.hasOwn(LABELS, attributes[KEYS.choice]) ? attributes[KEYS.choice] : '';
    // Frühere Warte-Regeln nicht ohne neue Auswahl in diesen Wunsch umdeuten.
    if (choice === 'wait' && attributes[KEYS.label] !== LEGACY_LABELS.wait) choice = '';
    if (saved) choice = saved.choice;
    return { choice, wishes: choice === 'replace' ? cleanWishes(saved ? saved.wishes : attributes[KEYS.wishes]) : '',
      customerNote: customerNoteFrom(note, attributes) };
  }

  const needsCleanup = (note, attributes, state) =>
    Object.values(KEYS).some(key => text(attributes[key]) !== '') ||
    text(note) !== orderNoteFor(state);

  const dirty = () => !same(draft, confirmed);
  const renderAll = () => instances.forEach(element => element.render());
  const aborted = () => new DOMException('Optional save cancelled', 'AbortError');
  async function requestCart(suffix, options = {}, signal) {
    if (signal?.aborted) throw aborted();
    const controller = new AbortController();
    const cancel = () => controller.abort();
    signal?.addEventListener('abort', cancel, { once: true });
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart${suffix}`, {
        credentials: 'same-origin', cache: 'no-store', ...options,
        headers: { Accept: 'application/json', ...options.headers }, signal: controller.signal
      });
      if (!response.ok) throw new Error('Cart request failed');
      const cart = await response.json();
      if (signal?.aborted) throw aborted();
      if (!Array.isArray(cart.items) || !Number.isFinite(cart.item_count) || !cart.attributes) throw new Error('Invalid cart response');
      return cart;
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', cancel);
    }
  }

  async function waitForQuantities(signal) {
    if (signal?.aborted) throw aborted();
    const sync = window.AIMA?.cartSync;
    if (!sync) return;
    let timer;
    let cancel;
    try {
      await Promise.race([
        sync.flush(),
        new Promise((_, reject) => {
          timer = setTimeout(() => reject(new Error('Cart busy')), 15000);
          cancel = () => reject(aborted());
          signal?.addEventListener('abort', cancel, { once: true });
        })
      ]);
      if (signal?.aborted) throw aborted();
      if (sync.blocked) throw new Error('Cart state uncertain');
    } finally {
      clearTimeout(timer);
      if (cancel) signal?.removeEventListener('abort', cancel);
    }
  }

  function persist() {
    clearTimeout(saveTimer);
    if (saving) return saving;
    if (!dirty() && !needsRetry) {
      phase = ''; renderAll(); return Promise.resolve(true);
    }
    const controller = new AbortController();
    saveController = controller;
    saving = (async () => {
      while (dirty() || needsRetry) {
        phase = 'saving'; renderAll();
        try {
          await waitForQuantities(controller.signal);
          const latest = await requestCart('.js', {}, controller.signal);
          // Fremde bzw. neuere Bestellhinweise erhalten, solange der Kunde sie hier nicht bearbeitet hat.
          if (draft.customerNote === confirmed.customerNote) {
            const customerNote = customerNoteFrom(latest.note, latest.attributes);
            draft = { ...draft, customerNote };
            confirmed = { ...confirmed, customerNote };
          }
          const target = { ...draft };
          const attributes = attributesFor();
          const note = orderNoteFor(target);
          const block = blockFor(target);
          if (block) knownBlocks.add(block);
          const cart = await requestCart('/update.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note, attributes })
          }, controller.signal);
          if (text(cart.note) !== note || !Object.entries(attributes).every(([key, value]) => text(cart.attributes[key]) === value)) {
            throw new Error('Preference not confirmed');
          }
          confirmed = target;
          needsRetry = false;
          document.dispatchEvent(new CustomEvent('aima:replacement:saved', { detail: { attributes, note } }));
        } catch {
          needsRetry = true; phase = 'error'; renderAll(); return false;
        }
      }
      phase = ''; renderAll(); return true;
    })().finally(() => {
      if (saveController === controller) saveController = null;
      saving = null;
    });
    return saving;
  }

  function updateDraft(next, delay = 0) {
    if (same(next, draft) && !needsRetry) return;
    draft = next; revision += 1;
    clearTimeout(saveTimer);
    phase = 'saving'; renderAll();
    if (delay) saveTimer = setTimeout(persist, delay);
    else void persist();
  }

  function connectForm(form) {
    if (!form) return;
    // Das sichtbare Notizfeld zeigt nur den freien Kundentext. Ein gemeinsames
    // verborgenes note-Feld übermittelt Kundentext UND Wunsch an Shopify.
    form.querySelectorAll('textarea[name="note"], input[name="note"]:not([data-replacement-order-note])').forEach(input => {
      input.setAttribute('data-replacement-customer-note', '');
      input.removeAttribute('name');
      input.value = draft.customerNote;
    });
    if (forms.has(form)) return;
    forms.add(form);
    form.addEventListener('input', event => {
      if (!event.target.matches('[data-replacement-customer-note]')) return;
      updateDraft({ ...draft, customerNote: text(event.target.value) }, 500);
    }, true);
    form.addEventListener('change', event => {
      if (!event.target.matches('[data-replacement-customer-note]')) return;
      // Verhindert, dass die bestehende Theme-Autospeicherung unseren Wunsch überschreibt.
      event.stopImmediatePropagation();
      updateDraft({ ...draft, customerNote: text(event.target.value) }, 500);
      void persist();
    }, true);
    form.addEventListener('submit', async event => {
      // Erneutes Auslösen verwendet weiterhin die normale Validierung aller Apps.
      if (resubmitting.has(form) || event.defaultPrevented) return;
      if (!saving && !dirty() && !needsRetry) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (continuing.has(form)) return;
      continuing.add(form);
      const submitter = event.submitter;
      let timer;
      try {
        // Die optionale AJAX-Speicherung darf den Checkout nicht festhalten.
        // note und attributes stehen synchron im normalen POST-Formular.
        const saved = await Promise.race([
          persist().catch(() => false),
          new Promise(resolve => { timer = setTimeout(() => resolve(false), CHECKOUT_SAVE_WAIT_MS); })
        ]);
        if (!saved) {
          clearTimeout(saveTimer);
          saveController?.abort();
        }
      } finally {
        clearTimeout(timer);
        // Auch ein sofortiger Speicherfehler muss den ersten Submit vollständig
        // beenden lassen, bevor requestSubmit erneut ausgelöst werden kann.
        await new Promise(resolve => setTimeout(resolve, 0));
        // Kein form.submit() und keine direkte Checkout-Weiterleitung:
        // Shopify-, Theme- und Zapiet-Prüfungen laufen weiterhin regulär.
        try {
          if (form.isConnected) {
            resubmitting.add(form);
            try {
              form.requestSubmit(submitter?.form === form ? submitter : undefined);
            } finally { resubmitting.delete(form); }
          }
        } finally { continuing.delete(form); }
      }
    }, true);
  }

  class ReplacementPreference extends HTMLElement {
    connectedCallback() {
      instances.add(this);
      if (this.initialized) { connectForm(this.closest('form')); this.render(); this.observeHeading(); return; }
      this.initialized = true;
      this.find = name => this.querySelector(`[data-replacement-${name}]`);
      this.fields = this.find('fields');
      this.note = this.find('note');
      let migrate = false;
      if (!confirmed) {
        const attributes = {};
        this.querySelectorAll('[data-replacement-field]').forEach(input => {
          attributes[KEYS[input.dataset.replacementField]] = input.value;
        });
        if (this.dataset.initialBlock) knownBlocks.add(this.dataset.initialBlock);
        confirmed = stateFrom(this.dataset.initialNote, attributes);
        draft = { ...confirmed };
        migrate = needsCleanup(this.dataset.initialNote, attributes, confirmed);
        if (migrate) needsRetry = true;
      }
      connectForm(this.closest('form'));
      this.fields.disabled = false;
      this.fields.addEventListener('change', event => {
        const input = event.target.closest('[data-replacement-choice]');
        if (!input?.checked || !Object.hasOwn(LABELS, input.value)) return;
        updateDraft({ ...draft, choice: input.value });
        this.closeOptions();
      });
      this.fields.addEventListener('click', event => {
        const input = event.target.closest('[data-replacement-choice]');
        // Auch ein erneuter Klick auf die bereits gewählte Option klappt zu.
        if (input?.checked && input.value === draft.choice) this.closeOptions();
      });
      this.note.addEventListener('input', () => updateDraft({ ...draft, wishes: cleanWishes(this.note.value) }, 500));
      this.note.addEventListener('blur', () => { void persist(); });
      this.find('clear').addEventListener('click', () => updateDraft({ ...draft, choice: '', wishes: '' }));
      this.find('retry').addEventListener('click', () => { void persist(); });
      this.render();
      this.observeHeading();
      if (migrate) void persist();
    }
    observeHeading() {
      const heading = this.querySelector('.aima-replacement__heading');
      if (!heading?.querySelector('.aima-replacement__heading-line')) return;
      this.resizeHeading ||= () => this.fitHeading();
      this.fitHeading();
      if (window.ResizeObserver) {
        this.headingObserver ||= new ResizeObserver(entries => {
          const width = entries[0].contentRect.width;
          if (width === this.headingWidth) return;
          this.headingWidth = width;
          cancelAnimationFrame(this.headingFrame);
          this.headingFrame = requestAnimationFrame(this.resizeHeading);
        });
        this.headingObserver.observe(heading);
      } else {
        window.addEventListener('resize', this.resizeHeading);
      }
      document.fonts?.ready.then(this.resizeHeading);
    }
    fitHeading() {
      if (!this.isConnected) return;
      const heading = this.querySelector('.aima-replacement__heading');
      const available = heading?.getBoundingClientRect().width;
      if (!available) return;
      heading.style.removeProperty('--replacement-heading-size');
      const lines = Array.from(heading.querySelectorAll('.aima-replacement__heading-line'));
      let size = parseFloat(getComputedStyle(heading).fontSize);
      // Nachmessen berücksichtigt auch Rundungen der tatsächlich geladenen Schrift.
      for (let attempt = 0; attempt < 8; attempt += 1) {
        const longest = Math.max(...lines.map(line => line.getBoundingClientRect().width));
        if (longest <= available) break;
        size = Math.max(1, Math.floor(Math.min(size - 0.25, size * (available - 1) / longest) * 100) / 100);
        heading.style.setProperty('--replacement-heading-size', size + 'px');
      }
    }
    disconnectedCallback() {
      instances.delete(this);
      this.headingObserver?.disconnect();
      cancelAnimationFrame(this.headingFrame);
      this.headingWidth = undefined;
      if (this.resizeHeading) window.removeEventListener('resize', this.resizeHeading);
    }
    closeOptions() {
      const panel = this.find('panel');
      if (!panel.open) return;
      this.find('note-details').open = false;
      panel.open = false;
      // Nach dem Zuklappen liegt der Tastaturfokus wieder auf der sichtbaren Zeile.
      panel.querySelector('summary').focus({ preventScroll: true });
    }
    render() {
      const attributes = attributesFor();
      this.querySelectorAll('[data-replacement-field]').forEach(input => {
        input.value = attributes[KEYS[input.dataset.replacementField]];
      });
      this.querySelectorAll('[data-replacement-choice]').forEach(input => { input.checked = input.value === draft.choice; });
      if (this.note.value !== draft.wishes) this.note.value = draft.wishes;
      this.find('note-details').hidden = draft.choice !== 'replace';
      this.find('clear').hidden = !draft.choice;
      this.find('selected').hidden = !draft.choice;
      this.find('selected').textContent = TITLES[draft.choice] || '';
      this.find('feedback').hidden = phase !== 'error';
      const form = this.closest('form');
      if (form) {
        form.querySelectorAll('[data-replacement-customer-note]').forEach(input => {
          if (input.value !== draft.customerNote) input.value = draft.customerNote;
        });
        this.find('order-note').value = orderNoteFor(draft);
        this.find('order-note').disabled = false;
      }
    }
  }
  customElements.define(tag, ReplacementPreference);

  window.addEventListener('pageshow', async event => {
    if (!event.persisted || !instances.size || saving || dirty() || needsRetry) return;
    const currentRevision = revision;
    try {
      const cart = await requestCart('.js');
      if (!saving && !dirty() && !needsRetry && revision === currentRevision) {
        confirmed = stateFrom(cart.note, cart.attributes); draft = { ...confirmed };
        phase = ''; renderAll();
        if (needsCleanup(cart.note, cart.attributes, confirmed)) {
          needsRetry = true;
          void persist();
        }
      }
    } catch { /* Das reguläre Formular enthält weiterhin den bekannten Hinweis. */ }
  });
})();